import React, { useMemo, useRef, useState } from "react";
import produce from "immer";
import qs from "qs";
import getConfig from "next/config";
import { useRouter } from "next/router";
import { string } from "prop-types";
import { useTranslation } from "@lib/i18n";
import { useDispatch } from "react-redux";
import { twMerge } from "tailwind-merge";

import api from "@api";

import ItemSelector from "./ItemSelector";
import TopBarItem from "./TopBarItem";
import CEEUnavailableModal, {
  CEE_UNAVAILABLE_MODAL_TYPE,
} from "@components/shared/CEEUnavailableModal";
import Icon from "@components/shared/Icons/Icon";
import ProfileIcon from "@components/shared/Icons/Profile";
import ShoppingCartIcon from "@components/shared/Icons/ShoppingCart";
import PhoneIcon from "@components/shared/Icons/PhoneIcon";
import Logo from "@components/Logo/Logo";
import SimpleButton from "@components/shared/SimpleButton";
import SellerContactModal from "@components/Header/SellerContactModal";

import { toast } from "@lib/reactToastify";
import { getFinanceVariant } from "@lib/getFinanceVariant";
import { getStaticAssetURL } from "@lib/getStaticAssetURL";

import { BUSINESS_MODELS, JOURNEY_TYPE, DEAL_STATES } from "@shared/constants";
import {
  useIsKeyAccount,
  useIsUserTrustedSelector,
} from "@redux/reducers/auth";
import { ROUTES } from "@shared/routing/routes";

import gtm from "@gtm/core";
import {
  homepageConfiguratorUpdateUrlEvent,
  changeConfigurationEvent,
} from "@gtm/events/homepage";
import {
  amiLogoClickEvent,
  accountLogoClickEvent,
  basketLogoClickEvent,
  switchBusinessModelEvent,
  displaySellerInformationEvent,
} from "@gtm/events/shared";

import { withTabNavigation } from "@hoc/withTabNavigation";

import { useAvailablePreconfigurations } from "@hooks/useAvailablePreconfigurations";
import { useConfigurationPathname } from "@hooks/useConfigurationPathname";
import { useDetectDevice } from "@hooks/useDetectDevice";
import { useGetIsTargetBusinessModelFinanceEnabled } from "@hooks/useGetIsTargetBusinessModelFinanceEnabled";
import { useIsB2BSelected } from "@hooks/useIsB2BSelected";
import { useRoutingUtils } from "@hooks/useRoutingUtils";
import { FIXED_ELEMENTS_ATTRIBUTE_NAME } from "@hooks/useScrollToView";

import { setAppDataAction } from "@redux/actions/configActions";
import {
  createNewDealFromExistingAction,
  updateIsSelectionConfirmedAction,
  setBusinessModelAction,
  setJourneyAction,
  patchCarConfigurationAction,
  refreshDealWithAppConfigAction,
  updateCarPreconfigurationAction,
  setVehicleQuantityAction,
  setB2BLeasysAction,
} from "@redux/actions/dealActions";
import { setLastChangedAtAction } from "@redux/actions/deprecatedActions";

import { logoutKeyAccountAction } from "@redux/actions/authActions";

import {
  useCEESubsidySelector,
  useConfigurationSelector,
  useIsBusinessModelSwitcherEnabledSelector,
  useIsContactPhoneNumberEnabledSelector,
  useLanguageSettingsSelector,
  useReferrerSelector,
} from "@redux/reducers/appConfig";
import {
  useCarConfigurationSelector,
  useBusinessModelSelector,
  useDealSelector,
  useActiveCarPreconfigurationSelector,
  useIsCEESubsidySelectedSelector,
  useIsB2BLeasysSelected,
} from "@redux/reducers/deal";

import { getCEESubsidy } from "@shared/v2/lib/getCEESubsidy";
import { getIsB2BEnabledForAnyJourney } from "@shared/v2/lib/getIsB2BEnabledForAnyJourney";
import { getJourneyNameFromJourneyType } from "@shared/v2/lib/getJourneyNameFromJourneyType";
import { getTargetJourney } from "@shared/v2/lib/getTargetJourney";

const { publicRuntimeConfig } = getConfig();
const { ROOT_URL_PREFIX } = publicRuntimeConfig;

const LogoWrapper = withTabNavigation("div");

const CLASSNAMES = {
  navItem: "flex text-primaryDark transition-colors duration-300",
  navItemButton:
    "text-primaryDark hover:text-primaryDark/80 font-normal tracking-px p-1 h-12 m-0 text-balance max-md:[&>span:last-child]:hidden",
  navItemButtonIcon: "size-9",
};

const Header = ({ dealState }) => {
  const [
    isCEESubsidyUnavailableModalOpen,
    setIsCEESubsidyUnavailableModalOpen,
  ] = useState(false);
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const routingUtils = useRoutingUtils();
  const isUserTrusted = useIsUserTrustedSelector();
  const router = useRouter();
  const configurationPathname = useConfigurationPathname();
  const { financeSimulation } = useDealSelector();
  const pageVariant = getFinanceVariant(financeSimulation.journey);
  const affiliation = useReferrerSelector();
  const { label } = useActiveCarPreconfigurationSelector();
  const isBusinessModelSwitcherEnabled = useIsBusinessModelSwitcherEnabledSelector();
  const configuration = useConfigurationSelector();
  const isB2BEnabled = getIsB2BEnabledForAnyJourney(configuration);
  const currentBusinessModel = useBusinessModelSelector();
  const isB2BSelected = useIsB2BSelected();
  const { allowedLanguages } = useLanguageSettingsSelector();
  const getIsTargetBusinessModelFinanceEnabled = useGetIsTargetBusinessModelFinanceEnabled();
  const isContactPhoneNumberHeaderEnabled = useIsContactPhoneNumberEnabledSelector();
  const {
    id: activePreconfigurationId,
  } = useActiveCarPreconfigurationSelector();
  const isKeyAccount = useIsKeyAccount();
  const isB2BLeasysSelected = useIsB2BLeasysSelected();

  const skipCEECheckRef = useRef(false);
  const nextBusinessModelAfterCEEConfirmRef = useRef("");

  const ceeSubsidySelected = useIsCEESubsidySelectedSelector();
  const ceeSubsidy = useCEESubsidySelector(ceeSubsidySelected);

  const availablePreconfigurations = useAvailablePreconfigurations();

  const isConfigurationPage = router.pathname.includes(ROUTES.CONFIG);

  const { service: selectedServices } = useCarConfigurationSelector();

  const { isMobile } = useDetectDevice();

  const wrapperAttributes = useMemo(
    () => ({
      [FIXED_ELEMENTS_ATTRIBUTE_NAME]: !isMobile,
    }),
    [isMobile]
  );

  const baseBusinessModels = [
    { value: BUSINESS_MODELS.B2C, title: t("header.b2cTitle") },
  ];

  const availableBusinessModels = produce(baseBusinessModels, draft => {
    if (isBusinessModelSwitcherEnabled && isB2BEnabled) {
      draft.push({
        value: BUSINESS_MODELS.B2B,
        title: t("header.b2bTitle"),
      });
    }
  });

  const isMultipleLanguages = allowedLanguages.length > 1;

  const availableLanguages =
    isMultipleLanguages &&
    allowedLanguages.map(language => {
      return {
        value: language,
        title: t(`header.languages.${language}`),
      };
    });

  const onPreconfigurationChange = preconfiguration => {
    if (preconfiguration.id === activePreconfigurationId) {
      return;
    }

    dispatch(updateCarPreconfigurationAction(preconfiguration));

    dispatch(setLastChangedAtAction(Date.now()));

    gtm.fire(changeConfigurationEvent(preconfiguration.label));
  };

  const changeBusinessModel = async targetBusinessModel => {
    if (currentBusinessModel === targetBusinessModel) {
      return;
    }

    const targetJourneyType = getTargetJourney(
      configuration,
      targetBusinessModel,
      financeSimulation.journey
    );

    if (isKeyAccount) {
      if (isB2BLeasysSelected) {
        dispatch(setB2BLeasysAction(false));
      }
    }

    if (
      !skipCEECheckRef.current &&
      ceeSubsidy?.enabled &&
      !!ceeSubsidySelected
    ) {
      const nextCeeSubsidy = getCEESubsidy(
        getJourneyNameFromJourneyType(targetJourneyType),
        { configuration },
        targetBusinessModel
      );

      if (!nextCeeSubsidy?.enabled) {
        setIsCEESubsidyUnavailableModalOpen(true);
        nextBusinessModelAfterCEEConfirmRef.current = targetBusinessModel;

        return;
      }
    }

    skipCEECheckRef.current = false;

    const { data } = await api.getAppData();
    const { car } = data;
    const { preconfigurations } = car;

    const isTargetBusinessModelB2B =
      targetBusinessModel === BUSINESS_MODELS.B2B;

    const singlePreconfiguration =
      preconfigurations.find(config =>
        isTargetBusinessModelB2B ? config.b2bDefault : config.default
      ) || preconfigurations[0];

    onPreconfigurationChange(singlePreconfiguration);

    const nextServices = selectedServices.filter(service =>
      isTargetBusinessModelB2B ? service.b2B : service.b2C
    );

    dispatch(patchCarConfigurationAction({ service: nextServices }));

    let session = {
      businessModel: targetBusinessModel,
      configuration: {
        service: nextServices[0]?.id ?? null,
      },
      quantity: 1,
    };

    dispatch(setVehicleQuantityAction(1));

    if (!getIsTargetBusinessModelFinanceEnabled(targetBusinessModel)) {
      dispatch(setJourneyAction(JOURNEY_TYPE.CASH));

      session.journey = JOURNEY_TYPE.CASH;
    }

    dispatch(setBusinessModelAction(targetBusinessModel));

    await api.updateSession(session);

    if (isUserTrusted) {
      await dispatch(createNewDealFromExistingAction());
    }

    gtm.fire(
      switchBusinessModelEvent({
        isB2BSelected: isTargetBusinessModelB2B,
        label: isTargetBusinessModelB2B
          ? t("header.b2bTitle")
          : t("header.b2cTitle"),
      })
    );

    gtm.fire(
      homepageConfiguratorUpdateUrlEvent({
        pageVariant,
        pageTitle: router.asPath,
        affiliation,
        isB2BSelected: isTargetBusinessModelB2B,
        price: financeSimulation.totalPrice.cash.netPrice,
        variant: label,
      })
    );
  };

  const handleCEEUnavailableStay = () => {
    setIsCEESubsidyUnavailableModalOpen(false);
  };

  const handleCEEUnavailableContinue = () => {
    skipCEECheckRef.current = true;

    setIsCEESubsidyUnavailableModalOpen(false);

    changeBusinessModel(nextBusinessModelAfterCEEConfirmRef.current);
  };

  const onAmiLogoClick = () => {
    gtm.fire(
      amiLogoClickEvent({
        label: isB2BSelected ? "home ami pro" : "home ami",
      })
    );

    router.push(ROUTES.CONFIG, configurationPathname);
  };

  const onMyProfileIconClick = () => {
    // prevent reload when on login page
    if (router.pathname === ROUTES.MY_ACCOUNT_LOGIN) {
      return;
    }

    gtm.fire(accountLogoClickEvent());

    const targetRoute = isUserTrusted
      ? ROUTES.MY_ACCOUNT_INDEX
      : ROUTES.MY_ACCOUNT_LOGIN;

    const localizedPathname = routingUtils.getLocalizedPathname(
      targetRoute,
      null,
      i18n.language
    );

    return router.push(targetRoute, localizedPathname);
  };

  const [isSellerInfoOpen, setIsSellerInfoOpen] = useState(false);

  const onSellerInformationClick = () => {
    if (isMobile) {
      window.location.href = `tel:${t("header.sellerContactModal.number")}`;
      return;
    }
    setIsSellerInfoOpen(true);
    gtm.fire(displaySellerInformationEvent());
  };

  const onShoppingCartIconClick = async () => {
    if (dealState === DEAL_STATES.OFFER) {
      const localizedOfferSummaryPathname = routingUtils.getLocalizedPathname(
        ROUTES.OFFER_SUMMARY,
        null,
        i18n.language
      );

      return router.push(ROUTES.OFFER_SUMMARY, localizedOfferSummaryPathname);
    }

    if (
      dealState === DEAL_STATES.ORDER &&
      router.pathname !== ROUTES.ORDER_CONFIRMATION
    ) {
      await dispatch(createNewDealFromExistingAction());
    }

    if (router.pathname === ROUTES.ORDER_CONFIRMATION) {
      await dispatch(updateIsSelectionConfirmedAction(false));
    }

    gtm.fire(basketLogoClickEvent());

    const localizedBasketPathname = routingUtils.getLocalizedPathname(
      ROUTES.BASKET,
      null,
      i18n.language
    );

    return router.push(ROUTES.BASKET, localizedBasketPathname);
  };

  const redirectToTranslatedUrl = () => {
    // TODO config page gets handled by applyConfigurationToUrl; consider refactoring to have both event-driven
    if (router.pathname === ROUTES.CONFIG) {
      return;
    }

    const query = qs.stringify(router.query, { addQueryPrefix: true });

    const params = routingUtils.getParamsFromPathname(
      router.asPath.replace(ROOT_URL_PREFIX, "").replace(/\?.+$/, "")
    );

    const nextAsPath = routingUtils.getLocalizedPathname(
      router.pathname,
      params,
      i18n.language
    );

    router.replace(router.pathname + query, nextAsPath, {
      shallow: true,
    });
  };

  const changeLocale = async nextLanguage => {
    await Promise.all([
      i18n.changeLanguage(nextLanguage),
      api.updateSession({
        language: nextLanguage,
      }),
    ]);

    try {
      const { data: appData } = await api.getAppData();

      dispatch(setAppDataAction(appData));
      dispatch(refreshDealWithAppConfigAction());

      redirectToTranslatedUrl();
    } catch (error) {
      toast.error(
        t("notify.apiDefaultError"),
        {
          toastId: "brandid-registration-redirect-error",
        },
        error
      );
    }
  };

  const removeKeyAccount = async () => {
    await api.updateSession({ isB2BLeasysSelected: false });
    dispatch(logoutKeyAccountAction());

    router.push(ROUTES.CONFIG, configurationPathname);
  };

  const citroenLogoWithLabel = getStaticAssetURL("/header/citroen_Logo.svg");
  const citroenLogoWithoutLabel = getStaticAssetURL(
    "/header/citroen_Logo_no_label.svg"
  );

  const citroenLogoUrl = isMobile
    ? citroenLogoWithoutLabel
    : citroenLogoWithLabel;

  return (
    <header
      className="flex flex-col items-center min-h-30 bg-white/90 backdrop-blur-md lg:sticky lg:top-0 lg:left-0 lg:right-0 lg:z-40 border-b border-b-greyLight"
      {...wrapperAttributes}
    >
      <div className="flex justify-center w-full max-md:border-b border-b-primaryDark/10">
        <div className="flex justify-between w-full max-w-pageContent p-1 sm:px-4">
          <div
            className="flex justify-end flex-wrap gap-2 items-center w-full"
            data-id="business-model-container"
          >
            <ItemSelector
              className="mr-auto"
              value={currentBusinessModel}
              items={availableBusinessModels}
              onClickItem={changeBusinessModel}
              isDisabled={!isConfigurationPage}
            />
            <CEEUnavailableModal
              isOpen={isCEESubsidyUnavailableModalOpen}
              dataId="cee-subsidy-unavailable-modal"
              onCancel={handleCEEUnavailableStay}
              onConfirm={handleCEEUnavailableContinue}
              type={CEE_UNAVAILABLE_MODAL_TYPE.BUSINESS_MODEL}
            />

            {isMultipleLanguages && (
              <ItemSelector
                value={i18n.language}
                items={availableLanguages}
                onClickItem={changeLocale}
              />
            )}
            {isContactPhoneNumberHeaderEnabled && (
              <TopBarItem onClick={onSellerInformationClick}>
                <span className="flex items-center gap-1">
                  <Icon className="size-4" icon={<PhoneIcon />} />
                  {t("header.sellerContactModal.number")}
                </span>
              </TopBarItem>
            )}
            {isKeyAccount && (
              <TopBarItem
                isSelected
                isSelectedHoverable
                onClick={removeKeyAccount}
              >
                {t("header.keyAccountLogout")}
              </TopBarItem>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center w-full max-w-pageContent px-1 min-h-22 lg:grid lg:grid-cols-[2fr_1fr_2fr] sm:px-4 text-primaryDark">
        <div className="flex items-center justify-center mr-auto">
          <LogoWrapper
            aria-label="AMI"
            className="mx-2 sm:ml-3 lg:mx-1 transition duration-150 hover:scale-105 active:scale-95"
            onClick={onAmiLogoClick}
          >
            <Logo />
          </LogoWrapper>
        </div>

        <div className="flex items-center justify-center self-center max-lg:-order-1 border-r border-r-primaryDark pr-2 sm:pr-3 lg:border-r-0 lg:pr-0">
          <LogoWrapper
            className="w-10 smallMobile:w-14 transition duration-150 hover:scale-105 active:scale-95"
            onClick={onAmiLogoClick}
          >
            <img
              alt="Citroën"
              className="w-full cursor-pointer"
              data-id="img-brand-logo"
              src={citroenLogoUrl}
              title="Citroën"
            />
          </LogoWrapper>
        </div>

        <SellerContactModal
          isOpen={isSellerInfoOpen}
          closeModal={() => setIsSellerInfoOpen(false)}
        />

        <div className="flex justify-center items-center ml-auto">
          <nav>
            <ul className="flex gap-3 list-none">
              <li className={CLASSNAMES.navItem} data-id="header-myAccount">
                <SimpleButton
                  aria-label={t("header.myAccount")}
                  className={twMerge(
                    CLASSNAMES.navItemButton,
                    "gap-3 largeMobile:gap-1"
                  )}
                  onClick={onMyProfileIconClick}
                  startAdornment={
                    <span className="relative block shrink-0 grow-0 size-9 basis-9">
                      <Icon
                        className={CLASSNAMES.navItemButtonIcon}
                        icon={<ProfileIcon />}
                      />
                      {isUserTrusted && (
                        <span
                          className="absolute right-0 -bottom-1.5"
                          data-id="header-checkmark-trusted"
                        >
                          <Icon
                            className="size-4 bg-white text-primaryDark pt-px pl-px rounded-full text-xs/1"
                            icon="✔"
                          />
                        </span>
                      )}
                    </span>
                  }
                >
                  <span className="hidden md:block">
                    {t("header.myAccount")}
                  </span>
                </SimpleButton>
              </li>
              {availablePreconfigurations.length > 0 && (
                <li
                  className={CLASSNAMES.navItem}
                  data-id="header-shoppingCart"
                >
                  <SimpleButton
                    aria-label={t("header.shoppingCart")}
                    className={CLASSNAMES.navItemButton}
                    onClick={onShoppingCartIconClick}
                    startAdornment={
                      <Icon
                        className={CLASSNAMES.navItemButtonIcon}
                        icon={<ShoppingCartIcon />}
                      />
                    }
                  >
                    <span className="hidden md:block">
                      {t("header.shoppingCart")}
                    </span>
                  </SimpleButton>
                </li>
              )}
            </ul>
          </nav>
        </div>
      </div>
    </header>
  );
};

Header.propTypes = {
  className: string,
  dealState: string,
};

export default Header;
